<template>
  <TModalCritical
    v-model:visible="authenticationModalShown"
    :title="modalTitle"
    :subtitle="modalSubtitle"
    enable-scroll
    @close="handleHideModal"
  >
    <AppAuth @toggle-auth-form="toggleAuth" />
  </TModalCritical>
</template>

<script setup lang="ts">
import { AUTHENTICATION_PROVIDERS } from '~/constants/authentication'

import type { SignupExitedParamsResponseEnum } from '~/types/tracking'
import type { ModalCloseTrigger } from '~/components/_tailwind/modal/types'

const { routeBaseName } = useBaseName()
const localePath = useLocalePath()
const {
  authenticationIntent,
  isSignupVisible,
  showSignup,
} = useSignup()
const enquiryOwnerName = useEnquiryOwnerName()
const { isLoginVisible, showLogin } = useLogin()
const { title: modalTitle, subtitle: modalSubtitle } = useAuthHeading()

const {
  authenticationModalShown,
  authenticationProvider,
  authenticationType,
  finishProfile,
  hideAuthModal,
  isLoggedIn,
} = useAuthentication()

const profileCompletionPending = useProfileCompletionPending()

watch(
  [authenticationType, profileCompletionPending],
  () => {
    if (routeBaseName.value === 'lp-signup') return

    authenticationModalShown.value = Boolean(authenticationType.value) || profileCompletionPending.value
  },
  { immediate: true },
)

const { isRenderedOwnerPage } = useBaseName()
const isOwner = computed(() => {
  return authenticationIntent.value === 'ListingIntent' || isRenderedOwnerPage.value
})

function trackSignupExitedEvent({ trigger, provider }: { trigger: SignupExitedParamsResponseEnum, provider: 'email' | undefined }) {
  trackSignupExited({
    isOwner: isOwner.value,
    response: trigger,
    signupType: provider,
  })
}

watch(
  isSignupVisible,
  (newVal, oldVal) => {
    if (newVal) {
      trackSignupViewed({
        signupTypes: Object.values(AUTHENTICATION_PROVIDERS),
        isOwner: isOwner.value,
        cta: routeBaseName.value,
        pageSource: routeBaseName.value,
      })
    }
    else if (isLoginVisible.value && oldVal !== undefined) {
      trackSignupExitedEvent({
        trigger: 'login',
        provider: authenticationProvider.value ? 'email' : undefined,
      })
    }
  },
  {
    immediate: true,
  },
)

const externalAuthError = useExternalAuthError()

async function handleHideModal(trigger: ModalCloseTrigger | SignupExitedParamsResponseEnum) {
  const newTrigger = trigger === 'button' ? 'close' : trigger

  if (profileCompletionPending.value) {
    externalAuthError.value = null

    if (authenticationIntent.value === 'BookingIntent') {
      usePendingRequestToBookUrlCookie().value = null
      profileCompletionPending.value = false

      hideAuthModal()

      return
    }

    await finishProfile()

    return
  }

  if (isSignupVisible.value) {
    trackSignupExitedEvent({
      trigger: newTrigger,
      provider: authenticationProvider.value ? 'email' : undefined,
    })
  }

  externalAuthError.value = null
  hideAuthModal()

  if (routeBaseName.value && ['index-signin', 'index-signup', 'lp-signup'].includes(routeBaseName.value)) {
    navigateTo(localePath('index'))
  }
}

function toggleAuth() {
  externalAuthError.value = null

  if (authenticationType.value === 'Login') {
    showSignup({ intent: authenticationIntent.value, ownerName: enquiryOwnerName.value })
  }
  else if (authenticationType.value === 'Signup') {
    showLogin({ intent: authenticationIntent.value })
  }
}

onBeforeUnmount(async () => {
  if (authenticationModalShown.value && !isLoggedIn.value) {
    await handleHideModal('navigation')
  }
})
</script>

<i18n src="~~/i18n/locales/common/auth/signup.json" lang="json" />

<i18n src="~~/i18n/locales/common/auth/login.json" lang="json" />

<i18n src="~~/i18n/locales/common/auth/profile.json" lang="json" />
